import { globalComponents } from 'src/types/globalTypes';
import CertificatePage from '../CertificatePage';
import Captcha from 'src/components/ui-kit/Captcha';
import Input from 'src/components/ui-kit/Input';
import { useCallback, useEffect, useState } from 'react';
import ResultLoading from 'src/components/ui-kit/ResultLoading';
import {
  ContentWrapper,
  MainContent,
  SubContent,
  Item,
  ItemTitle,
  ItemContent,
  CaptchaDiv,
  ButtonDiv,
  Button,
  Annotation,
  ErrorMsg,
} from 'src/components/contents/certification/CertificatePage';
import { useModalStore, useStore } from 'src/hooks/useStore';
import {
  CaseType,
  CertDownCode,
  IReqCertDownload,
  IResCertDownload,
} from 'src/types/api/certificateApiTypes';
import CertificateResult from '../CertificateResult';
import { FaDownload, FaTimesCircle } from 'react-icons/fa';
import { CustomRadioButton } from 'src/styles/radio';
import { certificateServiceProvider } from 'src/api/service/certificateService';
import useRequest from 'src/api/useRequest';
import AlertModal from 'src/components/ui-kit/AlertModal';
import { errorHandler } from 'src/api/errorHandler';
import { getCaptchaErrMsg } from 'src/utils/indexs';

const certificateService = certificateServiceProvider();
const initCertDownloadRes = {
  status: '',
  msg: '',
  pdfUrl: '',
};
/**
 * 電子證書下載
 */
const CertDownload: React.FC = () => {
  const isMobile = useStore((state) => state.isMobile);
  const setActiveModal = useModalStore((state) => state.setActiveModal);

  const [certNumber, setCertNumber] = useState<string>('');
  const [caseType, setCaseType] = useState<string>('');
  const [downloadCode, setDownloadCode] = useState<string>('');
  const [captchaUriSuffix, setCaptchaUriSuffix] = useState(Date.now());
  const [captchaUuid, setCaptchaUuid] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // HealthCheck 結果
  const [isCheckLoading, , healthStatus] = useRequest<string>(
    useCallback(() => certificateService.getHealthStatus(), []),
  );
  const [isUnHealthy, setIsUnHealthy] = useState(false);
  // 驗證碼檢查
  const [isCaptchaOk, setIsCaptchaOk] = useState<boolean>(false);
  const [captchaErrMsg, setCaptchaErrMsg] = useState<string>('請填寫驗證碼');
  // 回傳結果
  const [res, setRes] = useState<IResCertDownload>();
  // 請求參數
  const [reqParams, setReqParams] = useState<IReqCertDownload>();
  // 頁面靜態資料
  const component = globalComponents.C;
  const colorType = component.color;
  const subtitle = '下載證書';
  //欄位檢查
  const [isChecked, setIsChecked] = useState(false);

  // 插入請求資料
  useEffect(() => {
    setReqParams({
      certNo: certNumber,
      dwnCode: downloadCode,
      caseType: caseType,
      captchaUuid: captchaUuid,
      captcha: captcha,
    });
  }, [certNumber, downloadCode, captcha, captchaUuid, caseType]);

  // 進行API HealthCheck
  useEffect(() => {
    !isCheckLoading && setIsUnHealthy(healthStatus === 'N' ? true : false);
  }, [isCheckLoading, healthStatus]);

  useEffect(() => {
    const getReloadCaptcha = async () => {
      setCaptchaUriSuffix(Date.now());
      setCaptcha('');
      setDownloadCode('');
      setCertNumber('');
      setRes(initCertDownloadRes);
    };
    getReloadCaptcha();
  }, [caseType]);

  const handleSubmit = useCallback(async () => {
    setIsChecked(true);
    const isCheckOk = () => {
      return (
        reqParams?.captchaUuid &&
        reqParams?.captcha &&
        reqParams.certNo &&
        reqParams.dwnCode &&
        reqParams.caseType
      );
    };
    if (isCheckOk() && reqParams) {
      try {
        setIsSubmitting(true);
        const downloadRes = await certificateService.getCertDownload(reqParams);
        setRes(downloadRes);
        setIsChecked(false);
      } catch (e) {
        const errorMsg = errorHandler(e);
        setIsCaptchaOk(false);
        errorMsg && setCaptchaErrMsg(getCaptchaErrMsg(errorMsg));
        setRes(initCertDownloadRes);
      }
      setIsSubmitting(false);
    }
  }, [reqParams]);

  // 顯示結果
  const title = res?.status === CertDownCode.D00 ? '下載證書' : '無法下載證書';
  const result = isSubmitting ?
  <CertificateResult isSubmitting={isSubmitting} isChecked={true}>
          資料存取中<ResultLoading color={component.color.second} width={22} height={22} />
  </CertificateResult> : res?.status && (
    <CertificateResult title={title} isSubmitting={isSubmitting} isChecked={res.status === CertDownCode.D00}>
      {res.status === '0' ? (
        <>
          <a href={res.pdfUrl} rel="noreferrer" target="_blank">
            <FaDownload />
            {reqParams?.certNo}
          </a>
        </>
      ) : (
        <>
          <span>
            <FaTimesCircle />
          </span>
          <span>{res.msg}</span>
        </>
      )}
    </CertificateResult>
  );
  
  // 處理鍵盤觸發（Enter 或 Space）
  const handleKeyDown = (e: React.KeyboardEvent) => {
  	if (e.key === 'Enter' || e.key === ' ') {
  		e.preventDefault(); // 防止 Space 鍵捲動頁面
		if (!isUnHealthy) {
			handleSubmit();
		}
      }
  };

  return (
    <>
      <CertificatePage
        subtitle={subtitle}
        bannerBgImg={component.bgImgResultBanner}
        image={component.hoverImage}
        imgDot={component.bgImgDot}
        bgColor={colorType}
      >
        <ContentWrapper>
          <MainContent>
            <SubContent color={colorType.fifth}>
              <Item>
                <ItemTitle required><label htmlFor="input-caseType">案件種類<em>(必填)</em></label></ItemTitle>
                <ItemContent>
                  <div>
                    {Object.keys(CaseType).map((key) => (
                      <CustomRadioButton primaryColors={component.color} key={key}>
                        <label>
                          <input
                            type="radio"
                            value={key}
                            name="caseType"
							id="input-caseType"
                            onChange={() => {
                              setCaseType(key);
                            }}
                          />
                          <span />
                          {`${CaseType[key as keyof typeof CaseType]}`}
                        </label>
                      </CustomRadioButton>
                    ))}
                  </div>
                  {isChecked && !caseType && <ErrorMsg>請選擇案件種類。</ErrorMsg>}
                </ItemContent>
              </Item>
              {caseType && (
                <Item>
                  <ItemTitle required><label htmlFor="input-certNumber">
                  {caseType === 'P' ? '專利證書號數' : '商標註冊號數'}<em>(必填)</em>
                    {!isMobile && <br />}
					</label>
                  </ItemTitle>
                  <ItemContent>
                    <Input
                      value={certNumber}
                      placeholder={caseType === 'P' ? '專利 ex : M626197' : '商標 ex : 02220515'}
                      onChange={setCertNumber}
                      width="100%"
                      primaryColors={colorType}
					  inputId="input-certNumber"
                    />
                    {isChecked && !certNumber && (
                      <ErrorMsg>請填寫專利證書號或商標註冊號。</ErrorMsg>
                    )}
                  </ItemContent>
                </Item>
              )}
              <Item>
                <ItemTitle required><label htmlFor="input-downloadCode">下載碼<em>(必填)</em></label></ItemTitle>
                <ItemContent>
                  <Input
                    value={downloadCode}
                    placeholder={'請填寫'}
                    onChange={setDownloadCode}
                    width="100%"
                    primaryColors={colorType}
					inputId="input-downloadCode"
                  />
                  {isChecked && !downloadCode && <ErrorMsg>請填寫下載碼。</ErrorMsg>}
                </ItemContent>
              </Item>
              <Item>
                <ItemTitle required><label htmlFor="input-captcha">驗證碼<em>(必填)</em></label></ItemTitle>
                <ItemContent>
                  <CaptchaDiv>
                    <Input
                      value={captcha}
                      placeholder={'輸入驗證碼'}
                      onChange={setCaptcha}
                      width="100%"
                      primaryColors={colorType}
					  inputId="input-captcha"
                    />
                    <Captcha
                      captchaUuid={captchaUuid}
                      setCaptchaUuid={setCaptchaUuid}
                      captchaUriSuffix={captchaUriSuffix}
                      setCaptchaUriSuffix={setCaptchaUriSuffix}
                      color={colorType.third}
                    />
                  </CaptchaDiv>
                  {isChecked && !isCaptchaOk && !isSubmitting && <ErrorMsg>{captchaErrMsg}</ErrorMsg>}
                </ItemContent>
              </Item>
            </SubContent>
            <ButtonDiv>
              <Button
                disable={isUnHealthy}
                onClick={() => {
                  if (!isUnHealthy) {
                    handleSubmit();
                  }
                }}
				tabIndex={0} 
				role="button" 
				onKeyDown={handleKeyDown} 
				aria-label="下載"
              >
                下載
              </Button>
            </ButtonDiv>
          </MainContent>
          {result}
          <Annotation>若有任何疑問，請洽客服專線：(02)8176-9009。</Annotation>
        </ContentWrapper>
      </CertificatePage>
      {!isCheckLoading &&
        isUnHealthy &&
        setActiveModal(
          <AlertModal>
            <div>本功能暫停服務。</div>
          </AlertModal>,
        )}
    </>
  );
};

export default CertDownload;
